import {useEffect, useRef} from "react";
import {useSearchParams} from "react-router-dom";
import type {MessageWithMetadata, SourceInfo} from "../../../types";
import {parseDibUrl} from "../citation-preview/useCitationParts";
import {ISLAND_SEARCH_PARAM, useWorkIsland} from "./WorkIslandContext";

/**
 * Restores the work island from the "island" url param on mount, and
 * whenever the thread's messages arrive after the param is already there.
 * The Provider itself writes the param when the user opens or closes the
 * island, so this hook only ever reads it back.
 */
export function useWorkIslandUrlSync(messages: MessageWithMetadata[]): void {
    const [searchParams] = useSearchParams();
    const {request, openIsland} = useWorkIsland();
    const restoredEncodedUrlRef = useRef<string | null>(null);

    useEffect(() => {
        const encodedUrl = searchParams.get(ISLAND_SEARCH_PARAM);
        if (!encodedUrl) {
            restoredEncodedUrlRef.current = null;
            return;
        }
        if (request) {
            // The open island owns this param. A close removes the param one
            // render later, and that stale value must not reopen the island.
            restoredEncodedUrlRef.current = encodedUrl;
            return;
        }
        if (restoredEncodedUrlRef.current === encodedUrl) return;

        let url: string;
        try {
            url = decodeURIComponent(encodedUrl);
        } catch {
            return;
        }

        const matchedSource = findSourceByUrl(messages, url);
        if (matchedSource) {
            restoredEncodedUrlRef.current = encodedUrl;
            openIsland({kind: "topic", source: matchedSource});
            return;
        }

        if (messages.length === 0) {
            // The thread has not loaded yet. Wait for its messages.
            return;
        }

        // No message carries this url. Open with a minimal source built
        // from the url itself, so the topic still opens.
        restoredEncodedUrlRef.current = encodedUrl;
        openIsland({kind: "topic", source: buildMinimalSource(url)});
    }, [searchParams, request, messages, openIsland]);
}

function findSourceByUrl(
    messages: MessageWithMetadata[],
    url: string
): SourceInfo | null {
    for (const message of messages) {
        const found = message.sources?.find((source) => source.url === url);
        if (found) return found;
    }
    return null;
}

function buildMinimalSource(url: string): SourceInfo {
    const parsed = parseDibUrl(url);
    return {
        index: -1,
        id: parsed.topicId ?? "",
        topicId: parsed.topicId,
        name: "",
        segmentName: "",
        segmentId: parsed.segmentId ?? "",
        url,
        type: "Topic",
        description: "",
    };
}
